import type {
  AnalyseSpatiale,
  Intervention,
  InterventionCreation,
  ValidationStatut,
} from '@/models/interfaces/dashboard/Gouvernance'
import { apiClient } from '@/plugins/axios/api'
import { localeCourante } from '@/utils/libelleDynamique'

/**
 * Actions de gouvernance sur un équipement : analyse spatiale (§4, §3.5.3), décision du maire (§5),
 * registre des interventions et rapport PDF (§3.5).
 *
 * Toutes exigent que l'appelant gouverne la commune de l'équipement : le backend répond 403 sinon,
 * et 409 pour un équipement situé hors de tout périmètre communal.
 */
export class GouvernanceService {
  /** Plus proches voisins et zone de chalandise (§4.1, §4.2). */
  static async getAnalyseSpatiale(
    iftId: number,
    options: { rayon?: number; voisins?: number } = {},
  ): Promise<AnalyseSpatiale> {
    const params = new URLSearchParams()
    if (options.rayon !== undefined) params.set('rayon', String(options.rayon))
    if (options.voisins !== undefined) params.set('voisins', String(options.voisins))

    const response = await apiClient.get(`/api/infrastructures/${iftId}/analyse_spatiale`, {
      params,
    })
    return response.data as AnalyseSpatiale
  }

  /**
   * Enregistre la décision du maire (§5).
   *
   * Un rejet ou une demande de contre-expertise sans motif est refusé par le backend (422) : la
   * validation côté formulaire évite l'aller-retour, elle ne le remplace pas.
   */
  static async decider(iftId: number, statut: ValidationStatut, motif?: string | null) {
    const response = await apiClient.put(`/api/infrastructures/${iftId}/validation`, {
      statut,
      motif: motif?.trim() ? motif.trim() : null,
    })
    return response.data
  }

  /** Interventions enregistrées sur un équipement, les plus récentes d'abord. */
  static async getInterventions(iftId: number): Promise<Intervention[]> {
    // Filtre sur la colonne et non sur l'IRI : Infrastructure est exposée sous
    // /api/infrastructures/{id} alors que son identifiant Doctrine est iftId, et un filtre par IRI
    // échoue côté API.
    const response = await apiClient.get('/api/interventions', {
      params: { 'infrastructure.iftId': iftId },
    })
    // La collection arrive enveloppée en Hydra (apiClient demande application/ld+json) mais peut
    // aussi revenir en tableau nu selon le format négocié : les deux formes sont acceptées.
    const donnees = response.data
    if (Array.isArray(donnees)) return donnees as Intervention[]
    return (donnees['hydra:member'] ?? donnees['member'] ?? []) as Intervention[]
  }

  /** Programme une intervention (§5). */
  static async programmerIntervention(payload: InterventionCreation): Promise<Intervention> {
    const response = await apiClient.post('/api/interventions', payload)
    return response.data as Intervention
  }

  /**
   * Ouvre le rapport PDF de l'équipement (§3.5).
   *
   * Le fichier est récupéré en binaire par apiClient plutôt que par un lien direct : l'endpoint est
   * authentifié par en-tête Authorization, qu'un simple href n'emporte pas. Passer le jeton en
   * paramètre d'URL fonctionnerait moins bien encore — il se retrouverait dans l'historique du
   * navigateur et les journaux du serveur.
   */
  static async ouvrirRapportPdf(
    iftId: number,
    options: { rayon?: number; voisins?: number } = {},
  ): Promise<void> {
    const params = new URLSearchParams()
    if (options.rayon !== undefined) params.set('rayon', String(options.rayon))
    if (options.voisins !== undefined) params.set('voisins', String(options.voisins))
    // Rapport rédigé dans la langue de l'interface (libellés, dates, types et attributs traduits).
    params.set('langue', localeCourante())

    const response = await apiClient.get(`/api/infrastructures/${iftId}/rapport_pdf`, {
      params,
      responseType: 'blob',
      headers: { Accept: 'application/pdf' },
    })

    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
    window.open(url, '_blank', 'noopener')

    // L'onglet a chargé le blob à ce stade ; le libérer évite de retenir le PDF en mémoire.
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }
}
